import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Search, 
  TrendingUp, 
  TrendingDown, 
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { StockLogo } from '../utils/stockLogo';

const PortfolioIBKR = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const { formatMoney } = useCurrency(); // Assuming this context has generic formatting or I'll use Intl directly

  // Sabit account ID kullanıyoruz, isterseniz state'e alabiliriz.
  const ACCOUNT_ID = 'U15136454';

  useEffect(() => {
    fetchPositions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      // Proxy üzerinden istek atıyoruz
      const response = await axios.get(`/ibkr-api/portfolio/${ACCOUNT_ID}/positions/0`);
      if (Array.isArray(response.data)) {
        setPositions(response.data);
      } else {
        // Handle case where data might be nested
        console.warn('Unexpected data format:', response.data);
        setPositions(response.data.positions || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching positions:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  // Helper function to format currency
  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '-';
    return `${(value * 100).toFixed(2)}%`; // Assuming value is decimal like 0.12 for 12%
    // If API returns 12 for 12%, remove * 100
  };

  // Filter positions
  const filteredPositions = positions.filter(pos => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (pos.contractDesc || '').toLowerCase().includes(searchLower) ||
      (pos.ticker || '').toLowerCase().includes(searchLower) ||
      (pos.symbol || '').toLowerCase().includes(searchLower)
    );
  });

  // Sorting
  const sortedPositions = [...filteredPositions].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue, bValue;

    switch (sortConfig.key) {
      case 'holding':
        aValue = a.contractDesc || a.ticker || '';
        bValue = b.contractDesc || b.ticker || '';
        break;
      case 'shares':
        aValue = Number(a.position) || 0;
        bValue = Number(b.position) || 0;
        break;
      case 'costBasis':
        aValue = (Number(a.position) || 0) * (Number(a.avgCost) || 0);
        bValue = (Number(b.position) || 0) * (Number(b.avgCost) || 0);
        break;
      case 'currentValue':
        aValue = Number(a.mktValue) || ((Number(a.position) || 0) * (Number(a.mktPrice) || 0));
        bValue = Number(b.mktValue) || ((Number(b.position) || 0) * (Number(b.mktPrice) || 0));
        break;
      case 'unrealizedPnL':
        const aCost = (Number(a.position) || 0) * (Number(a.avgCost) || 0);
        const aVal = Number(a.mktValue) || ((Number(a.position) || 0) * (Number(a.mktPrice) || 0));
        aValue = Number(a.unrealizedPnl) || (aVal - aCost);

        const bCost = (Number(b.position) || 0) * (Number(b.avgCost) || 0);
        const bVal = Number(b.mktValue) || ((Number(b.position) || 0) * (Number(b.mktPrice) || 0));
        bValue = Number(b.unrealizedPnl) || (bVal - bCost);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedPositions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPositions.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-[#0A0E23]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0E23] p-6 pt-16">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Global Portfolio (IBKR)
          </h1>
          <div className="flex gap-2">
             {/* Action buttons could go here */}
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-[#151934] p-4 rounded-xl shadow-sm mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
             <div className="flex bg-gray-100 dark:bg-[#1f2544] p-1 rounded-lg">
                <button className="px-4 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-[#2a3052] shadow-sm text-gray-900 dark:text-white">
                  In holding currency
                </button>
                <button className="px-4 py-1.5 text-sm font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  In USD
                </button>
             </div>
             <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Show sold</span>
             </label>
          </div>

          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#1f2544] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Table Content */}
        <div className="bg-white dark:bg-[#151934] rounded-xl shadow-sm overflow-hidden">
          {error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="px-6 py-4 text-left">
                        <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500" />
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f2544]"
                        onClick={() => requestSort('holding')}
                      >
                        <div className="flex items-center gap-1">
                          Holding
                          {sortConfig.key === 'holding' && (
                            sortConfig.direction === 'asc' ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f2544]"
                        onClick={() => requestSort('shares')}
                      >
                         <div className="flex items-center justify-end gap-1">
                          Shares
                          {sortConfig.key === 'shares' && (
                            sortConfig.direction === 'asc' ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f2544]"
                        onClick={() => requestSort('costBasis')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Cost basis
                          {sortConfig.key === 'costBasis' && (
                            sortConfig.direction === 'asc' ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f2544]"
                        onClick={() => requestSort('currentValue')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Current value
                          {sortConfig.key === 'currentValue' && (
                            sortConfig.direction === 'asc' ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f2544]"
                        onClick={() => requestSort('unrealizedPnL')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Unrealized P&L
                          {sortConfig.key === 'unrealizedPnL' && (
                            sortConfig.direction === 'asc' ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {currentItems.map((item, index) => {
                      // Map API fields to UI - adapting based on typical IBKR fields
                      // contractDesc: Company Name
                      // ticker: Symbol
                      // position: Shares
                      // mktPrice: Current Price
                      // mktValue: Current Value
                      // avgCost: Cost Basis
                      // unrealizedPnl: Total Profit
                      // dailyPnl: Daily Profit (might not be directly available, verifying)
                      
                      const symbol = item.contractDesc || item.ticker || 'UNKNOWN';
                      const companyName = item.companyName || item.contractDesc || symbol; // Fallback
                      const shares = item.position || 0;
                      const price = item.mktPrice || 0;
                      const avgCost = item.avgCost || 0;
                      
                      const costBasisTotal = shares * avgCost;
                      const currentValueTotal = item.mktValue || (shares * price);
                      
                      const totalProfit = item.unrealizedPnl || (currentValueTotal - costBasisTotal);
                      const totalProfitPercent = costBasisTotal !== 0 ? (totalProfit / costBasisTotal) : 0;
                      
                      // Daily PnL might not be in the positions endpoint standardly without checking docs, 
                      // assuming a field 'dailyPnl' exists or we leave it 0
                      const dailyProfit = item.dailyPnl || 0;
                      const dailyProfitPercent = 0; // consistent data needed

                      const isProfitPositive = totalProfit >= 0;
                      const isDailyPositive = dailyProfit >= 0;

                      return (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-[#1f2544] transition-colors">
                          <td className="px-6 py-4">
                            <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500" />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="mr-3">
                                <StockLogo symbol={symbol} symbolInfo={{ description: companyName }} />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">{companyName}</div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500 dark:text-gray-400">{symbol}</span>
                                  <a 
                                    href={`https://www.tradingview.com/chart/?symbol=${symbol}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-indigo-500 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                    title="View on TradingView"
                                  >
                                    <ExternalLink size={14} />
                                  </a>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white font-medium">
                            {shares}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="text-sm text-gray-900 dark:text-white font-medium">{formatCurrency(costBasisTotal)}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(avgCost)}</div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="text-sm text-gray-900 dark:text-white font-medium">{formatCurrency(currentValueTotal)}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(price)}</div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className={`text-sm font-medium ${isProfitPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {isProfitPositive ? '+' : ''}{formatCurrency(totalProfit)}
                            </div>
                            <div className={`text-xs ${isProfitPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {isProfitPositive ? <TrendingUp size={12} className="inline mr-1" /> : <TrendingDown size={12} className="inline mr-1" />}
                              {(totalProfitPercent * 100).toFixed(2)}%
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                              <MoreHorizontal size={20} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPositions.length)} of {filteredPositions.length} results
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1f2544] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => paginate(i + 1)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === i + 1
                          ? 'bg-indigo-600 text-white'
                          : 'hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button 
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1f2544] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioIBKR;
